import { useRequiredContext } from "@redotech/react-util/context";
import ArrowRightIcon from "@redotech/redo-icons/arrow-right.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { useShippingNewSettings } from "@redotech/redo-merchant-app-common/shipping-new-settings-context";
import type { AskRedoCapabilityLinkCard } from "@redotech/redo-settings-common/ask-redo-capabilities";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, type ReactNode, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  getSettingsFocusedGroupHref,
  getSettingsFocusedSettingHref,
} from "../../../navigation/side-navigation/settings-agent/settings-deep-links";
import { getSettingsGroupFullPageHref } from "../../../navigation/side-navigation/settings-agent/settings-group-full-page-links";
import { useFullPageAskRedoNavigation } from "../../capability-block-navigation-context";
import type { CapabilityBlockData } from "../../capability-block-tool";
import * as styles from "./capability-block-card.module.css";

export const CapabilityBlockCard = memo(function CapabilityBlockCard({
  data,
}: {
  data: CapabilityBlockData;
}) {
  return CAPABILITY_BLOCK_RENDERERS[data.type](data);
});

const CapabilityLinkCard = memo(function CapabilityLinkCard({
  data,
}: {
  data: AskRedoCapabilityLinkCard;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);
  const fullPageNavigation = useFullPageAskRedoNavigation();
  const navigate = useNavigate();
  const shippingNewSettings = useShippingNewSettings();
  const href = getCapabilityHref(
    data.action.target,
    routes,
    shippingNewSettings,
  );
  const onNavigate = useCallback(() => {
    fullPageNavigation?.preserveConversation();
    navigate(href);
  }, [fullPageNavigation, href, navigate]);

  return (
    <Flex className={styles.card} dir="column" gap="md">
      <Flex dir="column" gap="xs">
        <Text fontSize="md" fontWeight="semibold" textColor="primary">
          {data.title}
        </Text>
        <Text fontSize="sm" textColor="secondary">
          {data.description}
        </Text>
      </Flex>
      <RedoButton
        className={styles.actionButton}
        disabled={fullPageNavigation !== null && !fullPageNavigation.threadId}
        hierarchy="secondary"
        IconTrailing={ArrowRightIcon}
        onClick={onNavigate}
        size="sm"
        text={data.action.label}
      />
    </Flex>
  );
});

type CapabilityBlockRendererRegistry = {
  [Type in CapabilityBlockData["type"]]: (
    data: Extract<CapabilityBlockData, { type: Type }>,
  ) => ReactNode;
};

const CAPABILITY_BLOCK_RENDERERS = {
  link_card: (data) => <CapabilityLinkCard data={data} />,
} satisfies CapabilityBlockRendererRegistry;

export const CapabilityBlockFallbackCard = memo(
  function CapabilityBlockFallbackCard() {
    return (
      <Flex className={styles.errorCard} dir="column" gap="xs" role="alert">
        <Text fontSize="sm" fontWeight="semibold" textColor="primary">
          This page could not be opened
        </Text>
        <Text fontSize="xs" textColor="secondary">
          Ask Redo to open it again, or use the navigation menu.
        </Text>
      </Flex>
    );
  },
);

type CapabilityRoutes = Parameters<
  typeof getSettingsGroupFullPageHref
>[0]["routes"];

function getCapabilityHref(
  target: AskRedoCapabilityLinkCard["action"]["target"],
  routes: CapabilityRoutes,
  shippingNewSettings: boolean,
): string {
  switch (target.type) {
    case "settings_group": {
      const href = getSettingsGroupFullPageHref({
        groupId: target.groupId,
        routes,
        shippingNewSettings,
      });
      return target.settingId
        ? getSettingsFocusedSettingHref({
            groupId: target.groupId,
            href,
            settingId: target.settingId,
          })
        : getSettingsFocusedGroupHref({ groupId: target.groupId, href });
    }
    case "support_setup":
      return `${routes.legacySupportBaseUrl}/setup`;
  }
}
